<template>
  <main role="main" class="py-4">
    <b-container>
      <h4>{{ $t("editRegion") }}</h4>

      <!-- Form cập nhật thông tin khu vực kinh doanh -->
      <b-form class="mt-4 max-width-700" @submit.prevent="submitForm">
        <b-form-row>
          <!-- Trường mã khu vực (ẩn) -->
          <b-form-group class="col-lg-12" style="display:none">
            <template v-slot:label>
              {{ $t("regionCode") }}<span class="text-danger">*</span>
            </template>
            <b-form-input type="text" minlength="1" maxlength="30" v-model="code" />
          </b-form-group>

          <!-- Tên khu vực kinh doanh (bắt buộc) -->
          <b-form-group class="col-lg-12">
            <template v-slot:label>
              {{ $t("regionName") }} <span class="text-danger">*</span>
            </template>
            <b-form-input
              type="text"
              :placeholder="$t('inputNameRegion')"
              required
              minlength="2"
              maxlength="50"
              v-model="name"
            />
          </b-form-group>

          <!-- Tiêu đề đếm số gói cước và nút mở modal thêm gói cước -->
          <b-form-group class="col-lg-12">
            <template v-slot:label>
              <b>{{ $t("selectedPackageCount", { count: selectedSubs ? selectedSubs.length : 0 }) }}</b>
            </template>
            <a class="btn btn-outline-primary btn-cus" @click="showModal" style="cursor:pointer">
              + {{ $t("add") }}
            </a>
          </b-form-group>

          <!-- Danh sách các gói cước đang được gán cho khu vực này -->
          <b-form-group class="col-lg-12">
            <div style="max-height: 430px !important; overflow-y: auto;">
              <b-list-group class="contacts-list-group">
                <div v-if="!selectedSubs || !selectedSubs.length" class="py-3 text-center text-secondary">
                  {{ $t("noData") }}
                </div>
                <b-list-group-item v-for="sub in (selectedSubs || [])" :key="sub.id || sub.subscriptionPlanId">
                  <subscription-plan-item :plan="sub" />
                </b-list-group-item>
              </b-list-group>
            </div>
          </b-form-group>
        </b-form-row>

        <!-- Nút Cập nhật khu vực -->
        <div class="text-right">
          <b-button type="submit" variant="primary" :disabled="isSubmitting">{{ $t("update") }}</b-button>
        </div>
      </b-form>
    </b-container>

    <!-- Component modal chọn gói cước dạng Transfer list dùng chung -->
    <subscription-plan-selection-modal
      ref="planSelectionModal"
      v-model="selectedSubs"
    />
  </main>
</template>

<script lang="ts">
import { Component, Ref } from "vue-property-decorator";
import { clone } from "lodash";
import * as regionService from "@/service/regionService";
import * as subscriptionPlanService from "@/service/subscriptionPlanService";
import BaseComponent from "@/components/BaseComponent";
import SubscriptionPlanItem from "@/views/location/components/SubscriptionPlanItem.vue";
import SubscriptionPlanSelectionModal from "@/views/location/components/SubscriptionPlanSelectionModal.vue";

/**
 * Màn hình Cập nhật khu vực kinh doanh (/location/edit/:id).
 * Luồng nghiệp vụ:
 * 1. Khởi tạo và nạp dữ liệu:
 *    - Nếu chuyển từ màn hình danh sách sang, nạp ngay lập tức dữ liệu initialRegion (0ms, không độ trễ).
 *    - Đồng thời gọi fetchPlan() chạy ngầm để đồng bộ dữ liệu mới nhất từ server.
 * 2. Thay đổi gói cước qua modal SubscriptionPlanSelectionModal.
 * 3. Gửi payload cập nhật { id, name, listIdSubcriptionPlans } lên server.
 */
@Component({
  components: {
    SubscriptionPlanItem,
    SubscriptionPlanSelectionModal
  }
})
export default class EditRegion extends BaseComponent {
  /** Tham chiếu đến modal chọn gói cước */
  @Ref("planSelectionModal") planSelectionModal!: SubscriptionPlanSelectionModal;

  /** ID của khu vực đang chỉnh sửa */
  id!: number;
  /** Tên khu vực */
  name = "";
  /** Mã khu vực */
  code = "";
  /** Trạng thái đang tải dữ liệu */
  isLoading = false;
  /** Trạng thái đang gửi form cập nhật */
  isSubmitting = false;
  /** Danh sách các gói cước đang được gán cho khu vực */
  selectedSubs: any[] = [];

  /**
   * Khởi tạo component:
   * - Lấy id từ route params.
   * - Nếu có initialRegion truyền từ bảng danh sách, hiển thị ngay lập tức.
   * - Gọi fetchPlan() để đồng bộ ngầm với server.
   */
  async mounted() {
    this.id = +this.$route.params.id;
    const initialRegion = (this.$route.params as any)?.initialRegion;
    if (initialRegion) {
      this.populateRegionData(initialRegion);
    }
    await this.fetchPlan();
  }

  /** Mở modal chọn gói cước */
  showModal() {
    this.planSelectionModal.showModal();
  }

  /**
   * Điền dữ liệu khu vực vào form:
   * - Chuẩn hóa các trường id và subscriptionPlanId của từng gói cước.
   * - Gán tên khu vực và danh sách gói cước vào state.
   */
  populateRegionData(regionData: any) {
    if (!regionData) return;
    if (regionData.name != null) {
      this.name = regionData.name;
    }

    let subs = regionData.subscriptions || [];

    (subs || []).forEach((sub: any) => {
      if (sub.id == null && sub.subscriptionPlanId != null) {
        sub.id = sub.subscriptionPlanId;
      }
      if (sub.subscriptionPlanId == null && sub.id != null) {
        sub.subscriptionPlanId = sub.id;
      }
    });

    if (subs.length > 0 || !this.selectedSubs || this.selectedSubs.length === 0) {
      this.selectedSubs = clone(subs) || [];
    }
  }

  /**
   * Lấy chi tiết thông tin khu vực từ server:
   * - Gọi getRegionDetail từ regionService.
   * - Nếu server chỉ trả về danh sách ID mà thiếu chi tiết đối tượng gói cước,
   *   tự động map từ getPlans() để hiển thị đầy đủ thông tin.
   */
  async fetchPlan() {
    try {
      this.isLoading = true;
      const plan = await regionService.getRegionDetail(this.id);
      const regionData = plan?.data || plan || {};

      this.populateRegionData(regionData);

      const listIds = regionData.listIdSubcriptionPlans || regionData.listIdSubscriptionPlans;
      if ((!this.selectedSubs || this.selectedSubs.length === 0) && Array.isArray(listIds) && listIds.length > 0) {
        const allPlans = (await subscriptionPlanService.getPlans()) || [];
        const subs = (allPlans || []).filter(p => listIds.includes(p.id));
        (subs || []).forEach((sub: any) => {
          if (sub.id == null && sub.subscriptionPlanId != null) sub.id = sub.subscriptionPlanId;
          if (sub.subscriptionPlanId == null && sub.id != null) sub.subscriptionPlanId = sub.id;
        });
        this.selectedSubs = clone(subs) || [];
      }
    } catch (e) {
      this.toastError(e);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Gửi form cập nhật khu vực:
   * - Lấy mảng ID gói cước đã chọn.
   * - Gọi API lưu khu vực.
   * - Thông báo thành công và chuyển về trang danh sách.
   */
  async submitForm() {
    try {
      this.isSubmitting = true;

      const selectedPlanIds = (this.selectedSubs || [])
        .map(item => (item.id != null ? item.id : item.subscriptionPlanId))
        .filter(Boolean);

      const data = {
        id: this.id,
        name: this.name,
        listIdSubcriptionPlans: selectedPlanIds,
      };

      await subscriptionPlanService.saveRegion(data);
      this.goBackToList();
    } catch (e) {
      this.toastError(e);
    } finally {
      this.isSubmitting = false;
    }
  }

  /** Chuyển hướng về trang danh sách khu vực kinh doanh và hiển thị thông báo thành công */
  goBackToList() {
    this.$router.push("/location");
    this.toastSuccessDelay(this.$t("updateRegionSuccessful").toString());
  }
}
</script>

<style lang="scss" scoped>
.btn-cus {
  float: right;
  margin-top: -30px;
}
</style>
